import React, { useEffect, useRef, useState } from 'react';

import { Pagination } from '../../../common/table/pagination/Pagination';
import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { Checkbox } from '../../../common/checkbox/Checkbox';
import Modal from '../../../common/modal/Modal';

import './VisitPackCreation.css';

export const VisitPackCreation = ({
  numberOfVisits,
  setNumberOfVisits,
  visit,
  editMode,
  handleAddFormsClick,
  closeModal,
  initialVisits,
  setVisitsToReturn,
  setVisitCodeEdited,
  pageSize,
  deleteVisit,
  canEditSchedule,
  initial,
  study,
  enrolmentVisitSet,
  originalVisitKey,
  enrolmentVisitAllowed = true,
  scheduleHasParticipantsLinked = false,
  existingVisitsForValidation = [],
}) => {
  const [currentPage, setCurrentPage] = useState(0);

  const originalVisitCodeRef = useRef(null);

  const [visits, setVisits] = useState(() => {
    if (editMode && visit) {
      return [visit];
    }

    if (initialVisits && initialVisits.length > 0) {
      return initialVisits;
    }

    return Array.from({ length: numberOfVisits }, (_, index) => {
      const isDefaultEnrolmentVisit = !!(initial && enrolmentVisitAllowed && index === 0);

      return {
        visitCode: '',
        visitType: '',
        enrolmentVisit: isDefaultEnrolmentVisit,
        day: isDefaultEnrolmentVisit ? '0' : '',
        windowBefore: isDefaultEnrolmentVisit ? '0' : '',
        windowAfter: isDefaultEnrolmentVisit ? '0' : '',
        longVisit: false,
        noOfForms: 0,
        formExternalReferences: [],
        availableForms: [],
      };
    });
  });

  const [errors, setErrors] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const nextEnrolmentVisitIndex = useRef(undefined);

  const restrictVisitEditForLinkedSchedule = !!scheduleHasParticipantsLinked && !!editMode && !!visit;

  const canEditField = (fieldName) => {
    if (!restrictVisitEditForLinkedSchedule) return true;

    return fieldName === 'visitCode' || fieldName === 'visitType' || fieldName === 'longVisit';
  };

  useEffect(() => {
    if (editMode && visit) {
      const originalKey = originalVisitKey || visit.visitCodeToUpdate || visit.visitCode;
      originalVisitCodeRef.current = originalKey;

      setVisits((prev) => {
        if (!prev?.length) return prev;

        const copy = [...prev];

        copy[0] = {
          ...copy[0],
          visitCodeToUpdate: originalKey,
          visitCodeEdited: false,
        };

        if (copy[0].enrolmentVisit) {
          copy[0].day = '0';
          copy[0].windowBefore = '0';
          copy[0].windowAfter = '0';
        }

        return copy;
      });
    }
  }, [editMode, visit, originalVisitKey]);

  const handleRemoveRow = (index) => {
    if (restrictVisitEditForLinkedSchedule) return;

    setVisits((prevVisits) => prevVisits.filter((_, i) => i !== index));
    setNumberOfVisits((prevCount) => prevCount - 1);
    setErrors((prevErrors) => prevErrors.filter((_, i) => i !== index));

    if (index / pageSize <= currentPage) {
      const page = Math.ceil((visits.length - 1) / pageSize);
      setCurrentPage(page - 1);
    }
  };

  const handleAddRowClick = () => {
    if (scheduleHasParticipantsLinked) return;

    const newVisit = {
      visitCode: '',
      visitType: '',
      enrolmentVisit: false,
      day: '',
      windowBefore: '',
      windowAfter: '',
      longVisit: false,
      noOfForms: 0,
      formExternalReferences: [],
      availableForms: [],
    };

    setVisits((prevVisits) => [...prevVisits, newVisit]);
    setNumberOfVisits((prevCount) => prevCount + 1);

    const page = Math.ceil((visits.length + 1) / pageSize);
    setCurrentPage(page - 1);
  };

  const handleInputChange = (index, fieldName, value) => {
    if (!canEditField(fieldName)) return;

    const currentVisit = visits[index];

    if (
      currentVisit?.enrolmentVisit &&
      (fieldName === 'day' || fieldName === 'windowBefore' || fieldName === 'windowAfter')
    ) {
      return;
    }

    if (
      (fieldName === 'day' || fieldName === 'windowBefore' || fieldName === 'windowAfter') &&
      (value === '-' || isNaN(value) || parseInt(value) < 0)
    ) {
      return;
    }

    setVisits((prevVisits) => {
      const updated = [...prevVisits];
      const current = updated[index];

      if (editMode && visit && fieldName === 'visitCode') {
        const originalKey = originalVisitCodeRef.current || current.visitCodeToUpdate || current.visitCode;

        updated[index] = {
          ...current,
          visitCode: value,
          visitCodeEdited: value !== originalKey,
          visitCodeToUpdate: originalKey,
        };

        setVisitCodeEdited(true);
        return updated;
      }

      updated[index] = { ...current, [fieldName]: value };
      return updated;
    });
  };

  const updateEnrolmentVisit = (index) => {
    if (restrictVisitEditForLinkedSchedule) return;

    const copyOfVisits = JSON.parse(JSON.stringify(visits));

    copyOfVisits.forEach((v, visitIndex) => {
      if (index === visitIndex) {
        v.enrolmentVisit = true;
        v.day = '0';
        v.windowBefore = '0';
        v.windowAfter = '0';
      } else {
        v.enrolmentVisit = false;
        v.day = '';
        v.windowBefore = '';
        v.windowAfter = '';
      }
    });

    setVisits(copyOfVisits);
  };

  const handleEnrolmentVisitChange = (index, checked) => {
    if (restrictVisitEditForLinkedSchedule) return;
    if (!enrolmentVisitAllowed) return;
    if (!checked) return;

    const foundDayValues = visits.some(
      (v) =>
        (v.day && v.day !== '0') ||
        (v.windowBefore && v.windowBefore !== '0') ||
        (v.windowAfter && v.windowAfter !== '0')
    );

    if (foundDayValues) {
      nextEnrolmentVisitIndex.current = index;
      setShowModal(true);
    } else {
      updateEnrolmentVisit(index);
    }
  };

  const handleLongVisitChange = (index, checked) => {
    if (!canEditField('longVisit')) return;

    setVisits((prevVisits) => {
      const updated = [...prevVisits];
      updated[index] = { ...updated[index], longVisit: checked };
      return updated;
    });
  };

  const validateRows = () => {
    const rowErrors = [];
    let isValid = true;

    const originalKey =
      editMode && visit ? originalVisitCodeRef.current || visit.visitCodeToUpdate || visit.visitCode : undefined;

    const existingVisitsExcludingCurrent = (existingVisitsForValidation || []).filter((existingVisit) => {
      if (!originalKey) return true;

      const existingKey = existingVisit.visitCodeToUpdate || existingVisit.visitCode;
      return existingKey !== originalKey;
    });

    const existingVisitCodes = existingVisitsExcludingCurrent
      .map((existingVisit) => String(existingVisit.visitCode || '').trim())
      .filter(Boolean);

    const existingDays = existingVisitsExcludingCurrent
      .map((existingVisit) => String(existingVisit.day ?? '').trim())
      .filter((day) => day !== '');

    const visitCodeCounts = visits.reduce((acc, v) => {
      const code = String(v.visitCode || '').trim();

      if (!code) return acc;

      acc[code] = (acc[code] || 0) + 1;
      return acc;
    }, {});

    const dayCounts = visits.reduce((acc, v) => {
      const day = String(v.day ?? '').trim();

      if (day === '') return acc;

      acc[day] = (acc[day] || 0) + 1;
      return acc;
    }, {});

    let shouldValidateDays = !enrolmentVisitAllowed;

    for (let i = 0; i < visits.length; i++) {
      const { visitCode, visitType, day, windowBefore, windowAfter, enrolmentVisit } = visits[i];

      if (enrolmentVisit || editMode || !enrolmentVisitAllowed) shouldValidateDays = true;

      const visitErrors = {};
      const trimmedVisitCode = String(visitCode || '').trim();
      const trimmedDay = String(day ?? '').trim();

      if (!trimmedVisitCode) {
        visitErrors.visitCode = '*REQUIRED';
      } else if (visitCodeCounts[trimmedVisitCode] > 1 || existingVisitCodes.includes(trimmedVisitCode)) {
        visitErrors.visitCode = '*VISIT CODE MUST BE UNIQUE';
      }

      if (!visitType) {
        visitErrors.visitType = '*REQUIRED';
      }

      if (!restrictVisitEditForLinkedSchedule) {
        if (!day && day !== 0 && shouldValidateDays) {
          visitErrors.day = '*REQUIRED';
        } else if (trimmedDay !== '' && (dayCounts[trimmedDay] > 1 || existingDays.includes(trimmedDay))) {
          visitErrors.day = '*DAY MUST BE UNIQUE';
        }

        if (!windowBefore && windowBefore !== 0 && shouldValidateDays) {
          visitErrors.windowBefore = '*REQUIRED';
        }

        if (!windowAfter && windowAfter !== 0 && shouldValidateDays) {
          visitErrors.windowAfter = '*REQUIRED';
        }
      }

      if (Object.keys(visitErrors).length) isValid = false;

      rowErrors.push(visitErrors);
    }

    setErrors(rowErrors);
    return isValid;
  };

  const handleAddFormAssignmentsClick = () => {
    const isValid = validateRows();
    if (!isValid) return;

    handleAddFormsClick(visits);
  };

  const handleSaveClick = () => {
    const isValid = validateRows();
    if (!isValid) return;

    if (visit) {
      const originalKey = originalVisitCodeRef.current || visit.visitCodeToUpdate || visit.visitCode;
      const edited = visits[0];

      setVisitsToReturn((prevVisits) => {
        const safePrev = Array.isArray(prevVisits) ? prevVisits : [];
        const updated = [...safePrev];

        const idx = updated.findIndex((v) => (v.visitCodeToUpdate || v.visitCode) === originalKey);
        if (idx === -1) return updated;

        updated[idx] = {
          ...updated[idx],
          ...edited,
          visitCodeToUpdate: originalKey,
        };

        return updated;
      });

      closeModal();
      return;
    }

    setNumberOfVisits((prevCount) => prevCount + visits.length);
    setVisitsToReturn((prevVisits) => [...(prevVisits || []), ...visits]);
    closeModal();
  };

  const handleCancelClick = () => closeModal();

  const handleDeleteClick = () => {
    if (restrictVisitEditForLinkedSchedule) return;

    deleteVisit(visit);
    closeModal();
  };

  const handleProceedClick = () => {
    updateEnrolmentVisit(nextEnrolmentVisitIndex.current);
    setShowModal(false);
  };

  const renderRows = () => {
    const startIndex = currentPage * pageSize;
    const endIndex = Math.min(startIndex + pageSize, visits.length);
    const enrolmentVisitIndex = visits.findIndex((v) => v.enrolmentVisit);

    return visits.slice(startIndex, endIndex).map((visitInList, localIndex) => {
      const index = startIndex + localIndex;
      const rowErrors = errors[index];

      const showDayInputs =
        !enrolmentVisitAllowed ||
        (enrolmentVisitIndex !== -1 && enrolmentVisitIndex <= index) ||
        (editMode && !visit && enrolmentVisitSet) ||
        (editMode &&
          (visitInList.day ||
            visitInList.day === 0 ||
            visitInList.windowBefore ||
            visitInList.windowBefore === 0 ||
            visitInList.windowAfter ||
            visitInList.windowAfter === 0));

      const isEnrolmentVisit = !!visitInList?.enrolmentVisit;
      const disableScheduleTimingFields = restrictVisitEditForLinkedSchedule;

      return (
        <tr className="visit-pack-row" key={index}>
          <td className="visit-pack-field visit-pack-field-index">{index + 1}</td>

          <td className="visit-pack-field visit-pack-field-visit-code">
            <input
              className={rowErrors?.visitCode ? 'visit-creation-table-input error' : 'visit-creation-table-input'}
              type="text"
              value={visitInList?.visitCode || ''}
              onChange={(e) => handleInputChange(index, 'visitCode', e.target.value)}
            />
            <p className="visit-pack-field-error">{rowErrors?.visitCode}</p>
          </td>

          <td className="visit-pack-field visit-pack-field-visit-type">
            <input
              className={rowErrors?.visitType ? 'visit-creation-table-input error' : 'visit-creation-table-input'}
              type="text"
              value={visitInList?.visitType || ''}
              onChange={(e) => handleInputChange(index, 'visitType', e.target.value)}
            />
            <p className="visit-pack-field-error">{rowErrors?.visitType}</p>
          </td>

          {enrolmentVisitAllowed &&
            (!editMode || (editMode && study?.visitPacks?.length === 0 && !enrolmentVisitSet)) && (
              <td className="visit-pack-field visit-pack-field-enrolment-visit">
                <Checkbox
                  checked={visitInList?.enrolmentVisit || false}
                  onChange={(e) => handleEnrolmentVisitChange(index, e)}
                  color="#6F8695"
                  fillStyle
                  disabled={disableScheduleTimingFields}
                />
              </td>
            )}

          <td className="visit-pack-field visit-pack-field-day">
            {showDayInputs ? (
              <>
                <input
                  className={`visit-creation-table-input ${rowErrors?.day ? 'error' : ''} ${
                    isEnrolmentVisit || disableScheduleTimingFields ? 'disabled-input' : ''
                  }`}
                  value={isEnrolmentVisit ? '0' : visitInList?.day ?? ''}
                  disabled={isEnrolmentVisit || disableScheduleTimingFields}
                  onChange={(e) => handleInputChange(index, 'day', e.target.value)}
                />
                <p className="visit-pack-field-error">{rowErrors?.day}</p>
              </>
            ) : (
              editMode && <p>-</p>
            )}
          </td>

          <td className="visit-pack-field visit-pack-field-window-before">
            {showDayInputs ? (
              <>
                <input
                  className={`visit-creation-table-input ${rowErrors?.windowBefore ? 'error' : ''} ${
                    isEnrolmentVisit || disableScheduleTimingFields ? 'disabled-input' : ''
                  }`}
                  value={isEnrolmentVisit ? '0' : visitInList?.windowBefore ?? ''}
                  disabled={isEnrolmentVisit || disableScheduleTimingFields}
                  onChange={(e) => handleInputChange(index, 'windowBefore', e.target.value)}
                />
                <p className="visit-pack-field-error">{rowErrors?.windowBefore}</p>
              </>
            ) : (
              editMode && <p>-</p>
            )}
          </td>

          <td className="visit-pack-field visit-pack-field-window-after">
            {showDayInputs ? (
              <>
                <input
                  className={`visit-creation-table-input ${rowErrors?.windowAfter ? 'error' : ''} ${
                    isEnrolmentVisit || disableScheduleTimingFields ? 'disabled-input' : ''
                  }`}
                  value={isEnrolmentVisit ? '0' : visitInList?.windowAfter ?? ''}
                  disabled={isEnrolmentVisit || disableScheduleTimingFields}
                  onChange={(e) => handleInputChange(index, 'windowAfter', e.target.value)}
                />
                <p className="visit-pack-field-error">{rowErrors?.windowAfter}</p>
              </>
            ) : (
              editMode && <p>-</p>
            )}
          </td>

          <td className="visit-pack-field visit-pack-field-long-visit">
            <Checkbox
              checked={visitInList?.longVisit || false}
              onChange={(e) => handleLongVisitChange(index, e)}
              color="#000000"
            />
          </td>

          {visits.length > 1 && !disableScheduleTimingFields && (
            <td className="visit-pack-field visit-pack-field-delete-button">
              <button
                className="visit-pack-delete-button"
                onClick={() => handleRemoveRow(index)}
                disabled={visitInList.enrolmentVisit}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="35"
                  height="35"
                  fill="red"
                  className="bi bi-x"
                  viewBox="0 0 16 16"
                >
                  <path
                    d="M11.354 4.146a.5.5 0 0 1 0 .708L9.207 8l2.147 2.146a.5.5 0 1 1-.708.708L8.5 8.707 6.354
                  10.854a.5.5 0 1 1-.708-.708L7.793 8 5.646 5.854a.5.5 0 0 1 .708-.708L8.5
                  7.293l2.146-2.147a.5.5 0 0 1 .708 0z"
                  />
                </svg>
              </button>
            </td>
          )}
        </tr>
      );
    });
  };

  return (
    <div className="tab-container">
      <div className="visit-pack-scrollable">
        <table className="visit-creation-table">
          <thead>
            <tr>
              <th className="visit-creation-header visit-creation-header-index" />
              <th className="visit-creation-header visit-creation-header-visit-code">Visit Code</th>
              <th className="visit-creation-header visit-creation-header-visit-type">Visit Type</th>

              {enrolmentVisitAllowed &&
                (!editMode || (editMode && study?.visitPacks?.length === 0 && !enrolmentVisitSet)) && (
                  <th className="visit-creation-header visit-creation-header-enrolment-visit">Enrolment Visit?</th>
                )}

              <th className="visit-creation-header visit-creation-header-day">
                {enrolmentVisitAllowed ? 'Days after Enrolment' : 'Days after Schedule Change'}
              </th>

              <th className="visit-creation-header visit-creation-header-window-before">
                <p>Window (days)</p>
                <h6 className="visit-creation-header">Before Target Date</h6>
              </th>

              <th className="visit-creation-header visit-creation-header-window-after">
                <p>&nbsp;</p>
                <h6 className="visit-creation-header">After Target Date</h6>
              </th>

              <th className="visit-creation-header visit-creation-header-long-visit">Long Visit?</th>

              {visits.length > 1 && !restrictVisitEditForLinkedSchedule && (
                <th className="visit-creation-header visit-creation-header-delete-button" />
              )}
            </tr>
          </thead>

          <tbody style={{ width: '100%' }}>{renderRows()}</tbody>
        </table>

        {!visit && !scheduleHasParticipantsLinked && (
          <div className="visit-pack-add-button">
            <SaveButton
              color="#74a47f"
              border="1px solid #74a47f"
              textColor="#ffffff"
              title="Add"
              onClick={handleAddRowClick}
            />
          </div>
        )}

        {visits.length > pageSize && (
          <div className="visit-pack-pagination">
            <Pagination
              pageCount={Math.ceil(visits.length / pageSize)}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        )}

        {visit && editMode && (
          <div className="button-container">
            <div className="button-edit-container">
              {canEditSchedule && !restrictVisitEditForLinkedSchedule && (
                <SaveButton
                  color="#ffffff"
                  border="1px solid #6f8594"
                  textColor="#6f8594"
                  title="Delete"
                  onClick={handleDeleteClick}
                />
              )}

              <SaveButton
                color="#ffffff"
                border="1px solid #6f8594"
                textColor="#6f8594"
                title="Cancel"
                onClick={handleCancelClick}
              />

              <SaveButton
                color="#6f8594"
                border="1px solid #6f8594"
                textColor="#ffffff"
                title="Save"
                onClick={handleSaveClick}
              />
            </div>
          </div>
        )}

        {!visit && editMode && (
          <div className="button-container">
            <div className="button-create-container">
              <SaveButton
                color="#6f8594"
                border="1px solid #6f8594"
                textColor="#ffffff"
                title="Save"
                onClick={handleSaveClick}
              />
            </div>
          </div>
        )}
      </div>

      {!editMode && (
        <div className="visit-pack-general-button-container">
          <div className="visit-pack-button-create-container">
            <SaveButton color="#deab26" title="Save & Add Form Assignments" onClick={handleAddFormAssignmentsClick} />
          </div>
        </div>
      )}

      {showModal && enrolmentVisitAllowed && (
        <Modal
          setIsModalOpen={setShowModal}
          headerTitle="Notice"
          width="540px"
          height="max-content"
          contentPadding="15px"
          justifyContent="center"
          hideButton
        >
          <div className="visit-pack-warning-modal">
            <p className="visit-pack-warning-modal-message">
              Changing the Enrolment Visit selection will reset any data entered in the "Days after Enrolment" and
              "Window (days)" columns.
            </p>

            <p className="visit-pack-warning-modal-message">
              Do you wish to proceed with altering the Enrolment Visit?
            </p>

            <div className="visit-pack-warning-modal-buttons">
              <SaveButton
                color="white"
                textColor="#deab26"
                border="1px solid #deab26"
                title="Cancel"
                onClick={() => setShowModal(false)}
              />

              <SaveButton color="#deab26" border="1px solid #deab26" title="Proceed" onClick={handleProceedClick} />
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
